<template>
  <div>
    <!-- 导航栏 -->
    <Nav />

    <!-- 案例详情主体内容 -->
    <!-- 案例图片展示区 -->
    <section class="mt-20 bg-white">
      <div class="max-w-7xl mx-auto px-4 py-8">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <!-- 左侧图片区 -->
          <div class="space-y-4">
            <div
              class="main-image-container relative overflow-hidden rounded-xl bg-gray-50"
            >
              <img
                id="mainImage"
                :src="currentMainImage || caseDetail.thumbnail_url"
                class="w-full h-96 object-cover cursor-zoom-in"
                @click="
                  openImageModal(currentMainImage || caseDetail.thumbnail_url)
                "
              />
              <button
                class="absolute top-4 right-4 w-10 h-10 bg-white bg-opacity-80 rounded-full flex items-center justify-center hover:bg-opacity-100 transition-all"
                @click="
                  openImageModal(currentMainImage || caseDetail.thumbnail_url)
                "
              >
                <i class="ri-zoom-in-line text-lg text-gray-700"></i>
              </button>
            </div>
            <div class="grid grid-cols-4 gap-3">
              <img
                v-for="(image, index) in caseDetail.image_urls"
                :key="index"
                :src="image"
                :class="[
                  'w-full h-20 object-cover rounded-lg cursor-pointer border-2',
                  index === activeImageIndex
                    ? 'border-primary thumbnail active'
                    : 'border-transparent thumbnail hover:border-primary',
                ]"
                @click="changeMainImage(image, index, $event)"
              />
            </div>
          </div>

          <!-- 右侧案例信息 -->
          <div class="space-y-6">
            <div class="flex items-start justify-between">
              <div class="flex-1 pr-6">
                <h1 class="text-3xl font-bold text-gray-900 mb-3">
                  {{ caseDetail.title }}
                </h1>
                <p class="text-gray-600 text-lg leading-relaxed">
                  {{ caseDetail.description }}
                </p>
              </div>
            </div>

            <!-- 标签与日期 -->
            <div class="flex items-center space-x-6 flex-wrap">
              <div class="flex space-x-2">
                <span
                  class="bg-blue-50 text-primary px-3 py-1 rounded-full text-sm"
                  >{{ caseDetail.category_name }}</span
                >
                <span
                  class="bg-green-50 text-green-600 px-3 py-1 rounded-full text-sm"
                  v-if="caseDetail.is_recommend == 1"
                  >精选推荐</span
                >
              </div>
              <div class="text-gray-600 text-sm">
                <i class="ri-calendar-line mr-1"></i>
                {{ $tool.formatDate(caseDetail.createtime, "YYYY-MM-DD") }}
              </div>
            </div>

            <!-- 项目信息 -->
            <div class="bg-gray-50 p-6 rounded-xl">
              <h3 class="text-lg font-semibold text-gray-900 mb-4">项目信息</h3>
              <div class="space-y-3">
                <div class="flex justify-between py-2 border-b border-gray-100">
                  <span class="text-gray-600">活动投入</span>
                  <span class="font-medium">￥{{ caseDetail.amount }}元</span>
                </div>
                <div class="flex justify-between py-2 border-b border-gray-100">
                  <span class="text-gray-600">活动规模</span>
                  <span class="font-medium">{{ caseDetail.scale }}</span>
                </div>
                <div class="py-2 flex items-start">
                  <span class="text-gray-600 w-16">主要设备</span>
                  <div class="flex-1 flex flex-wrap justify-end gap-2">
                    <span
                      v-for="(item, index) in caseDetail.item_list"
                      :key="index"
                      :class="[
                        'inline-flex items-center px-2 py-1 text-xs rounded',
                        getRandomColorClass(),
                      ]"
                    >
                      {{ item }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 案例详情内容 -->
    <section class="py-12 bg-gray-50">
      <div class="max-w-7xl mx-auto px-4">
        <h2 class="text-2xl font-bold text-gray-900 mb-8">案例详情</h2>
        <div class="bg-white rounded-xl shadow-sm overflow-hidden">
          <div class="p-8">
            <div
              class="prose max-w-none text-md leading-relaxed "
              v-html="caseDetail.content"
            ></div>
          </div>
        </div>
      </div>
    </section>

    <!-- 相关案例 -->
    <section class="py-12">
      <div class="max-w-7xl mx-auto px-4">
        <h2 class="text-2xl font-bold text-gray-900 mb-8">相关案例</h2>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div
            v-for="(relatedCase, index) in relatedCases"
            :key="index"
            class="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow"
            @click="goToCaseDetail(relatedCase.id)"
          >
            <div class="relative h-48 overflow-hidden">
              <img
                :src="relatedCase.thumbnail_url"
                class="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
              />
            </div>
            <div class="p-6">
              <h3 class="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                {{ relatedCase.title }}
              </h3>
              <p class="text-gray-600 text-sm mb-4 line-clamp-2">
                {{ relatedCase.brief_description }}
              </p>
              <div class="flex justify-between items-center">
                <span
                  class="bg-blue-50 text-primary px-3 py-1 rounded-full text-xs"
                  >{{ relatedCase.category_name }}</span
                >
                <span class="text-gray-500 text-sm">{{
                  relatedCase.createtime
                    ? $tool.formatDate(relatedCase.createtime, "YYYY-MM-DD")
                    : ""
                }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 在线咨询 -->
    <section class="py-12 bg-blue-50">
      <div class="max-w-7xl mx-auto px-4 text-center">
        <h2 class="text-2xl font-bold text-gray-900 mb-4">需要类似方案？</h2>
        <p class="text-gray-600 mb-8 max-w-2xl mx-auto">
          我们的专业团队可以根据您的需求，为您定制专属的会展设备解决方案
        </p>
        <button
          class="bg-primary text-white py-3 px-8 rounded-button hover:bg-secondary transition-colors inline-flex items-center justify-center space-x-2"
          @click="handleOnlineConsultation"
        >
          <i class="ri-headset-line"></i>
          <span>在线咨询</span>
        </button>
      </div>
    </section>

    <!-- 图片预览模态框 -->
    <div
      id="imageModal"
      class="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
      v-if="showPreview"
    >
      <div class="relative max-w-4xl max-h-full">
        <button
          @click="closeImageModal()"
          class="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
        >
          <i class="ri-close-line text-2xl"></i>
        </button>
        <img
          id="modalImage"
          :src="previewImageUrl"
          :alt="caseDetail.title || ''"
          class="max-w-full max-h-full object-contain"
        />
      </div>
    </div>

    <!-- 页脚 -->
    <Footer />
  </div>
</template>

<script>
import { Message } from "element-ui";
import Nav from "@/components/Nav.vue";
import Footer from "@/components/Footer.vue";
import api from "@/utils/api.js";

export default {
  name: "CaseDetail",
  components: {
    Nav,
    Footer,
  },
  data() {
    return {
      caseDetail: null,
      loading: true,
      error: "",
      showPreview: false,
      previewImageUrl: "",
      activeImageIndex: 0,
      imageList: [],
      currentMainImage: "",
      relatedCases: [],
    };
  },
  mounted() {
    this.loadCaseDetail();
    this.loadRelatedCases();
  },
  methods: {
    // 随机获取颜色类名，用于设备标签
    getRandomColorClass() {
      const colors = [
        "bg-red-50 text-red-700",
        "bg-green-50 text-green-700",
        "bg-blue-50 text-blue-700",
        "bg-yellow-50 text-yellow-700",
        "bg-purple-50 text-purple-700",
        "bg-pink-50 text-pink-700",
      ];
      return colors[Math.floor(Math.random() * colors.length)];
    },

    // 获取案例详情
    async loadCaseDetail() {
      try {
        this.loading = true;
        this.error = "";
        const caseId = this.$route.params.id; // 使用路由参数作为案例ID

        // 调用API获取案例详情
        const response = await api.caseshare.getDetail({ id: caseId });
        this.caseDetail = response.data;

        // 设置页面标题为案例名称
        if (this.caseDetail && this.caseDetail.title) {
          document.title = `${this.caseDetail.title} - 案例详情 - 会找我-专业会展设备租赁服务`;
        }
      } catch (err) {
        this.error = "案例信息加载失败，请稍后重试";
        console.error("加载案例详情失败:", err);
      } finally {
        this.loading = false;
      }
    },

    // 获取相关案例
    async loadRelatedCases() {
      try {
        const caseId = this.$route.params.id;

        // 调用API获取案例列表，设置参数获取相关案例
        // 假设API支持通过category_id或其他参数获取相关案例
        const categoryId = this.caseDetail ? this.caseDetail.category_id : null;
        const response = await api.caseshare.getList({
          category_id: categoryId,
          exclude_id: caseId, // 排除当前案例
          page: 1,
          pageSize: 3, // 获取3个相关案例
        });

        if (response.code === 200 && response.data && response.data.list) {
          this.relatedCases = response.data.list;
        }
      } catch (err) {
        console.error("加载相关案例失败:", err);
      }
    },

    // 打开图片预览
    openImageModal(imageUrl) {
      this.previewImageUrl = imageUrl;
      this.showPreview = true;
    },

    // 关闭图片预览
    closeImageModal() {
      this.showPreview = false;
    },

    // 切换主图
    changeMainImage(imageUrl, index, event) {
      if (event && event.target) {
        this.activeImageIndex = index;
        this.currentMainImage = imageUrl;
        this.previewImageUrl = imageUrl;
      }
    },

    // 查看相关设备
    viewRelatedProducts() {
      // 跳转到相关设备列表页
      this.$router.push({
        path: "/rent",
        query: { type: "led" },
      });
    },

    // 分享案例
    shareCase() {
      Message.success("分享链接已复制到剪贴板");
      // 实际项目中应该实现复制链接功能
    },

    // 在线咨询
    handleOnlineConsultation() {
      Message.success("正在连接客服，请稍候...");
    },

    // 跳转到其他案例详情
    goToCaseDetail(caseId) {
      this.$router.push(`/caseshare/${caseId}`);
    },
  },
};
</script>

<style scoped>
.mt-20 {
  margin-top: 5rem;
}

.prose {
  color: #4b5563;
  line-height: 1.6;
}

.prose h3 {
  color: #111827;
  font-size: 1.5rem;
  font-weight: 600;
  margin-top: 1.5rem;
  margin-bottom: 1rem;
}

.prose p {
  margin-bottom: 1rem;
}

.prose ul {
  margin-bottom: 1rem;
  padding-left: 1.5rem;
}

.prose li {
  margin-bottom: 0.5rem;
  list-style-type: disc;
}

.animate-fade-in-up {
  animation: fadeInUp 0.3s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.thumbnail.active {
  border-color: #2563eb !important;
}

#imageModal {
  backdrop-filter: blur(4px);
}
</style>



// WEBPACK FOOTER //
// src/pages/caseshare/CaseDetail.vue